import { usePage } from "@inertiajs/react";
import * as React from "react";
import { normalizeTitleKey } from "@/bizminer/lib/heatmap/normalizeTitleKey";
import type {
  BizminerHeatmapTaxonomy,
  BizminerPillarInsight,
} from "@/types/serializers";
import type { HeatmapKpiMappingProp } from "@/bizminer/App/Reports/useHeatmapState";
import KpiDriverCard from "./_components/KpiDrivers/KpiDriverCard";
import { withFunctionalInsightLayout } from "./withFunctionalInsightLayout";

interface KpiDriversProps {
  pillar_insight: BizminerPillarInsight;
  category_id: string;
  current_tab: string;
  heatmap_kpi_mapping: HeatmapKpiMappingProp;
  heatmap_taxonomy: BizminerHeatmapTaxonomy;
}

function heatmapGroupIdForFunctionalInsight(categoryId: string) {
  return categoryId.startsWith("ai_") ? categoryId.slice(3) : categoryId;
}

const TableHeader = () => (
  <div className="flex items-center justify-between px-4 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
    <span>KPI Driver</span>
    <span>Score</span>
  </div>
);

// Delay to allow Radix Collapsible animation to complete before scrolling
const ACCORDION_ANIMATION_DURATION = 300;

function KpiDrivers({
  pillar_insight,
  category_id,
  heatmap_kpi_mapping,
  heatmap_taxonomy,
}: KpiDriversProps) {
  const { url } = usePage();
  const targetDriverRef = React.useRef<HTMLDivElement>(null);
  const userInteractedRef = React.useRef(false);

  // Extract the driver key from ?driver= param for deep linking.
  // Value is a normalized-title slug (e.g. "evidence-that-ai-strategy").
  // Default: open first accordion when no param is present.
  const targetDriverKey = React.useMemo(() => {
    const urlObj = new URL(url, window.location.origin);
    return urlObj.searchParams.get("driver");
  }, [url]);

  // Scroll to target driver when it's rendered.
  // Cancel if the user clicks any accordion before the scroll fires.
  // Re-runs when targetDriverKey changes (new deep link or page navigation).
  React.useEffect(() => {
    userInteractedRef.current = false;

    if (targetDriverRef.current) {
      const timeoutId = setTimeout(() => {
        if (targetDriverRef.current && !userInteractedRef.current) {
          targetDriverRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, ACCORDION_ANIMATION_DURATION);

      return () => clearTimeout(timeoutId);
    }
  }, [targetDriverKey]);

  const category = pillar_insight.analysis?.categories?.find(
    (c) => c.id === category_id,
  );

  if (!category) {
    console.warn(`[KpiDrivers] Category not found: ${category_id}`, {
      availableCategories:
        pillar_insight.analysis?.categories?.map((c) => c.id) ?? [],
    });
  }

  const kpiDrivers = category?.kpi_drivers ?? [];
  const kpiDriverToSubcategoryLabel = React.useMemo(() => {
    const groupId = heatmapGroupIdForFunctionalInsight(category_id);
    const taxonomyGroup = heatmap_taxonomy?.groups?.find(
      (g) => g.id === groupId,
    );
    const mappingGroup = heatmap_kpi_mapping?.[groupId];

    const map = new Map<string, string>();
    if (!taxonomyGroup || !mappingGroup) return map;

    for (const subcategory of taxonomyGroup.tiles) {
      const questions = mappingGroup[subcategory.id];
      if (!Array.isArray(questions)) continue;
      for (const q of questions) {
        if (typeof q !== "string" || !q.trim()) continue;
        map.set(normalizeTitleKey(q), subcategory.label);
      }
    }

    return map;
  }, [category_id, heatmap_kpi_mapping, heatmap_taxonomy]);

  return (
    <div className="space-y-4">
      <TableHeader />

      {kpiDrivers.length === 0 ? (
        <div className="rounded-xl border border-border/50 bg-muted/10 px-4 py-8 text-center text-sm text-muted-foreground">
          No KPI drivers available for this category.
        </div>
      ) : (
        <div
          className="space-y-3"
          onPointerDown={() => {
            userInteractedRef.current = true;
          }}
        >
          {kpiDrivers.map((driver, idx) => {
            const driverSlug = normalizeTitleKey(driver.title).replace(
              / /g,
              "-",
            );
            const shouldOpenByDefault = targetDriverKey
              ? driverSlug === targetDriverKey
              : idx === 0;

            return (
              <div key={idx} ref={shouldOpenByDefault ? targetDriverRef : null}>
                <KpiDriverCard
                  driver={driver}
                  defaultOpen={shouldOpenByDefault}
                  heatmapSubcategoryLabel={kpiDriverToSubcategoryLabel.get(
                    normalizeTitleKey(driver.title),
                  )}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

KpiDrivers.layout = withFunctionalInsightLayout;

export default KpiDrivers;
